import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TICKET_STATUSES, type TicketStatus } from "../services/ticketService";

const MAIN_PATH: TicketStatus[] = [
  "OPEN",
  "ANALYZING",
  "ANALYZED",
  "APPROVED_FOR_DEV",
  "DEV_IN_PROGRESS",
  "DEV_READY",
  "MERGED",
  "LIVE",
];

const BRANCHES: { from: TicketStatus; statuses: TicketStatus[] }[] = [
  { from: "ANALYZING", statuses: ["SUPPORT_QUEUE", "NEEDS_CLARIFICATION"] },
  { from: "ANALYZED", statuses: ["CONFLICT_REVIEW", "AUTO_REJECTED"] },
  { from: "CONFLICT_REVIEW", statuses: ["REJECTED"] },
  { from: "DEV_IN_PROGRESS", statuses: ["DEV_FAILED"] },
];

function FlowChip({
  status,
  current,
  clickable,
  onSelect,
}: {
  status: TicketStatus;
  current: TicketStatus;
  clickable: boolean;
  onSelect?: (status: TicketStatus) => void;
}) {
  const { t } = useTranslation();
  const selected = status === current;
  return (
    <Chip
      size="small"
      color={selected ? "primary" : "default"}
      variant={selected ? "filled" : "outlined"}
      label={t(`status.${status}`)}
      onClick={clickable ? () => onSelect?.(status) : undefined}
      sx={{ fontWeight: selected ? 700 : 500 }}
    />
  );
}

function StatusRow({
  statuses,
  current,
  clickable,
  onSelect,
}: {
  statuses: TicketStatus[];
  current: TicketStatus;
  clickable: boolean;
  onSelect?: (status: TicketStatus) => void;
}) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
      {statuses.map((status, index) => (
        <Stack key={status} direction="row" spacing={0.5} alignItems="center">
          {index > 0 ? <ArrowForwardIcon sx={{ fontSize: 14, color: "text.secondary" }} /> : null}
          <FlowChip status={status} current={current} clickable={clickable} onSelect={onSelect} />
        </Stack>
      ))}
    </Stack>
  );
}

export function TicketStatusFlow({
  status,
  admin,
  onSelect,
}: {
  status: TicketStatus;
  admin?: boolean;
  onSelect?: (status: TicketStatus) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const select = (next: TicketStatus) => {
    if (!admin || next === status) return;
    onSelect?.(next);
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={t("tickets.lifecycle")}>
        <IconButton size="small" onClick={() => setOpen(true)} aria-label={t("tickets.lifecycle")}>
          <AccountTreeOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("tickets.lifecycle")}</DialogTitle>
        <DialogContent>
          {admin ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("tickets.lifecycleAdminHint")}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("tickets.lifecycleHint")}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t("tickets.lifecycleMain")}
          </Typography>
          <Box sx={{ mt: 1, mb: 2 }}>
            <StatusRow statuses={MAIN_PATH} current={status} clickable={Boolean(admin)} onSelect={select} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("tickets.lifecycleBranches")}
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {BRANCHES.map((branch) => (
              <Stack key={branch.from} direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <FlowChip status={branch.from} current={status} clickable={Boolean(admin)} onSelect={select} />
                <ArrowForwardIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <StatusRow statuses={branch.statuses} current={status} clickable={Boolean(admin)} onSelect={select} />
              </Stack>
            ))}
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t("tickets.lifecycleAll")}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {TICKET_STATUSES.map((item) => (
                <FlowChip
                  key={item}
                  status={item}
                  current={status}
                  clickable={Boolean(admin)}
                  onSelect={select}
                />
              ))}
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
